import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Button } from 'primereact/button';
import CBPromptModal from '../../shared/CBPromptModal';
import ConsolidatedInvoiceEditDialog from './ConsolidatedInvoiceEditDialog';
import {
  fetchConsolidatedInvoicePreview as _fetchConsolidatedInvoicePreview,
  markConsolidatedInvoiceReady as _markConsolidatedInvoiceReady,
  deleteConsolidatedInvoice as _deleteConsolidatedInvoice,
  exportConsolidatedInvoiceToNetvisor as _exportConsolidatedInvoiceToNetvisor,
  createConsolidatedInvoiceCreditNote as _createConsolidatedInvoiceCreditNote,
  copyConsolidatedInvoiceToDraft as _copyConsolidatedInvoiceToDraft,
} from '../../../actions/chainActions';
import { API_URL } from '../../../config';

function formatPrice(value) {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fi-FI');
}

var consolidatedStatusColors = {
  draft: { bg: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.7)', border: 'rgba(255, 255, 255, 0.2)' },
  ready: { bg: 'rgba(129, 199, 132, 0.2)', color: '#A5D6A7', border: 'rgba(129, 199, 132, 0.4)' },
  exported: { bg: 'rgba(100, 181, 246, 0.2)', color: '#90CAF9', border: 'rgba(100, 181, 246, 0.4)' },
};

function getStatusBadgeStyle(status) {
  var c = consolidatedStatusColors[status] || consolidatedStatusColors.draft;
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.color,
    border: '1px solid ' + c.border,
  };
}

var creditBadgeStyle = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: 'rgba(229, 115, 115, 0.2)',
  color: '#EF9A9A',
  border: '1px solid rgba(229, 115, 115, 0.4)',
};

var creditPendingBadgeStyle = {
  ...creditBadgeStyle,
  backgroundColor: 'rgba(255, 183, 77, 0.2)',
  color: '#FFB74D',
  border: '1px solid rgba(255, 183, 77, 0.4)',
};

var copyBadgeStyle = {
  ...creditBadgeStyle,
  backgroundColor: 'rgba(100, 181, 246, 0.2)',
  color: '#90CAF9',
  border: '1px solid rgba(100, 181, 246, 0.4)',
};

class ConsolidatedInvoiceDetail extends React.Component {
  state = {
    deleteModalOpen: false,
    exportModalOpen: false,
    editOpen: false,
    creditModalOpen: false,
    creditRequested: false,
    copyModalOpen: false,
    copyRequested: false,
  };

  handleEditSaved = function () {
    this.setState({ editOpen: false });
    // Preview grouping/subtotals must come from the server after an edit.
    this.props.fetchConsolidatedInvoicePreview(this.props.invoiceId);
  };

  componentDidMount() {
    this.props.fetchConsolidatedInvoicePreview(this.props.invoiceId);
  }

  componentDidUpdate(prevProps) {
    if (this.props.invoiceId !== prevProps.invoiceId) {
      this.props.fetchConsolidatedInvoicePreview(this.props.invoiceId);
    }
    var prevCredit = prevProps.createCreditNoteStatus;
    var currCredit = this.props.createCreditNoteStatus;
    if (this.state.creditRequested &&
        prevCredit && prevCredit.loading &&
        currCredit && !currCredit.loading) {
      this.setState({ creditRequested: false });
      if (!currCredit.error) {
        if (this.props.createdCreditNote && this.props.onNavigateToInvoice) {
          this.props.onNavigateToInvoice(this.props.createdCreditNote.id);
        } else {
          this.props.fetchConsolidatedInvoicePreview(this.props.invoiceId);
        }
      }
    }
    // Copy-to-draft: jump straight into the new draft so the correction
    // can be made right away.
    var prevCopy = prevProps.copyInvoiceStatus;
    var currCopy = this.props.copyInvoiceStatus;
    if (this.state.copyRequested &&
        prevCopy && prevCopy.loading &&
        currCopy && !currCopy.loading) {
      this.setState({ copyRequested: false });
      if (!currCopy.error && this.props.copiedInvoice && this.props.onNavigateToInvoice) {
        this.props.onNavigateToInvoice(this.props.copiedInvoice.id);
      }
    }
  }

  handleCreateCreditNote = function () {
    this.setState({ creditModalOpen: false, creditRequested: true });
    this.props.createConsolidatedInvoiceCreditNote(this.props.invoiceId);
  };

  handleCopyToDraft = function () {
    this.setState({ copyModalOpen: false, copyRequested: true });
    this.props.copyConsolidatedInvoiceToDraft(this.props.invoiceId);
  };

  navigateToInvoice = function (invoiceId) {
    if (this.props.onNavigateToInvoice) {
      this.props.onNavigateToInvoice(invoiceId);
    }
  };

  handleDownloadPdf = function () {
    window.open(API_URL + '/uiapi/1/chain_consolidated_invoice/' + this.props.invoiceId + '/report/', '_blank');
  };

  handleMarkReady = function () {
    this.props.markConsolidatedInvoiceReady(this.props.invoiceId);
  };

  handleExportToNetvisor = function () {
    this.props.exportConsolidatedInvoiceToNetvisor(this.props.invoiceId);
    this.setState({ exportModalOpen: false });
  };

  handleDelete = function () {
    this.props.deleteConsolidatedInvoice(this.props.invoiceId);
    this.setState({ deleteModalOpen: false });
    this.props.onClose();
  };

  render() {
    var t = this.props.t;
    var preview = this.props.consolidatedInvoicePreview;
    var previewStatus = this.props.consolidatedInvoicePreviewStatus;
    var onClose = this.props.onClose;
    var isLoading = previewStatus && previewStatus.loading;

    var header = preview && preview.header;
    var summary = preview && preview.summary;
    var vehicles = preview && preview.vehicles;

    var isDraft = header && header.status === 'draft';
    var isReady = header && header.status === 'ready';
    var isExported = header && header.status === 'exported';
    var exportStatus = this.props.exportInvoiceStatus;
    var blockExternalWrites = this.props.blockExternalWrites;
    var isCreditNote = header && !!header.credits_invoice_id;
    var creditNoteId = header ? header.credit_note_id : null;
    var creditNoteStatus = header ? header.credit_note_status : null;
    var createCreditNoteStatus = this.props.createCreditNoteStatus;
    var copyInvoiceStatus = this.props.copyInvoiceStatus;

    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.dialog} onClick={function (e) { e.stopPropagation(); }}>
          {/* Header */}
          <div style={styles.dialogHeader}>
            <h3 style={styles.dialogTitle}>{t('consolidatedInvoicePreview')}</h3>
            <Button
              icon="pi pi-times"
              text
              rounded
              onClick={onClose}
              style={{ color: '#fff', width: 32, height: 32 }}
            />
          </div>

          {isLoading || !preview ? (
            <div style={styles.loadingContainer}>
              <p style={styles.loadingText}>Ladataan...</p>
            </div>
          ) : (
            <div style={styles.dialogContent}>
              {/* Actions bar */}
              <div style={styles.actionsBar}>
                <Button
                  onClick={this.handleDownloadPdf.bind(this)}
                  label={t('downloadPdf')}
                  icon="pi pi-file-pdf"
                  size="small"
                  style={styles.actionButton}
                />
                {isDraft && !isCreditNote && (
                  <Button
                    onClick={function () { this.setState({ editOpen: true }); }.bind(this)}
                    label={t('edit')}
                    icon="pi pi-pencil"
                    size="small"
                    style={styles.actionButton}
                  />
                )}
                {isDraft && (
                  <Button
                    onClick={this.handleMarkReady.bind(this)}
                    label={t('markReady')}
                    icon="pi pi-check"
                    size="small"
                    style={{ ...styles.actionButton, backgroundColor: 'rgba(129, 199, 132, 0.2)', borderColor: 'rgba(129, 199, 132, 0.4)' }}
                  />
                )}
                {isReady && !blockExternalWrites && (
                  <Button
                    onClick={function () { this.setState({ exportModalOpen: true }); }.bind(this)}
                    label={exportStatus && exportStatus.loading ? t('exporting') : t('exportToNetvisor')}
                    icon="pi pi-upload"
                    size="small"
                    disabled={exportStatus && exportStatus.loading}
                    style={{ ...styles.actionButton, backgroundColor: 'rgba(100, 181, 246, 0.2)', borderColor: 'rgba(100, 181, 246, 0.4)' }}
                  />
                )}
                {isExported && !isCreditNote && !creditNoteId && (
                  <Button
                    onClick={function () { this.setState({ creditModalOpen: true }); }.bind(this)}
                    label={t('createCreditNote')}
                    icon="pi pi-replay"
                    size="small"
                    disabled={createCreditNoteStatus && createCreditNoteStatus.loading}
                    style={{ ...styles.actionButton, color: '#EF9A9A', borderColor: 'rgba(229, 115, 115, 0.4)' }}
                  />
                )}
                {isExported && !isCreditNote && (
                  <Button
                    onClick={function () { this.setState({ copyModalOpen: true }); }.bind(this)}
                    label={copyInvoiceStatus && copyInvoiceStatus.loading ? t('copying') : t('copyToDraft')}
                    icon="pi pi-copy"
                    size="small"
                    disabled={copyInvoiceStatus && copyInvoiceStatus.loading}
                    style={styles.actionButton}
                  />
                )}
                {isDraft && (
                  <Button
                    onClick={function () { this.setState({ deleteModalOpen: true }); }.bind(this)}
                    label={t('delete')}
                    icon="pi pi-trash"
                    size="small"
                    style={{ ...styles.actionButton, color: '#EF9A9A', borderColor: 'rgba(229, 115, 115, 0.4)' }}
                  />
                )}
              </div>

              {/* Pilot mode banner */}
              {blockExternalWrites && (
                <div style={styles.pilotBanner}>
                  {t('pilotModeNoExport')}
                </div>
              )}

              {/* Export error */}
              {exportStatus && exportStatus.error && (
                <div style={styles.errorBanner}>
                  {t('exportFailed')}: {exportStatus.error.detail || JSON.stringify(exportStatus.error)}
                </div>
              )}

              {/* Credit note creation error */}
              {createCreditNoteStatus && createCreditNoteStatus.error && (
                <div style={styles.errorBanner}>
                  {t('createCreditNoteFailed')}: {createCreditNoteStatus.error.detail || JSON.stringify(createCreditNoteStatus.error)}
                </div>
              )}

              {/* Copy-to-draft error */}
              {copyInvoiceStatus && copyInvoiceStatus.error && (
                <div style={styles.errorBanner}>
                  {t('copyToDraftFailed')}: {copyInvoiceStatus.error.detail || JSON.stringify(copyInvoiceStatus.error)}
                </div>
              )}

              {/* Exported info */}
              {isExported && header.exported_at && (
                <div style={styles.exportedInfo}>
                  <span style={styles.exportedLabel}>{t('exportedAt')}:</span>
                  <span style={styles.exportedValue}>{formatDate(header.exported_at)}</span>
                  {header.netvisor_sales_invoice_key && (
                    <React.Fragment>
                      <span style={styles.exportedLabel}>{t('netvisorKey')}:</span>
                      <span style={styles.exportedValue}>{header.netvisor_sales_invoice_key}</span>
                    </React.Fragment>
                  )}
                </div>
              )}

              {/* Invoice header info */}
              <div style={styles.headerInfo}>
                <div style={styles.headerRow}>
                  <div>
                    <span style={styles.headerLabel}>{t('customer')}</span>
                    <span style={styles.headerValue}>{header.customer_name || '-'}</span>
                  </div>
                  <div>
                    <span style={styles.headerLabel}>{t('businessId')}</span>
                    <span style={styles.headerValue}>{header.customer_business_id || '-'}</span>
                  </div>
                  <div>
                    <span style={styles.headerLabel}>{t('period')}</span>
                    <span style={styles.headerValue}>
                      {formatDate(header.period_start)} {'\u2013'} {formatDate(header.period_end)}
                    </span>
                  </div>
                  <div>
                    <span style={styles.headerLabel}>{t('status')}</span>
                    <span style={getStatusBadgeStyle(header.status || 'draft')}>
                      {header.status ? t('invoiceStatus' + header.status.charAt(0).toUpperCase() + header.status.slice(1)) : '-'}
                    </span>
                    {isCreditNote && (
                      <span
                        style={{ ...creditBadgeStyle, marginLeft: 6, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={function () { this.navigateToInvoice(header.credits_invoice_id); }.bind(this)}
                        title={t('openOriginalInvoice')}
                      >
                        {t('creditNote')} {'→'} #{header.credits_invoice_id}
                      </span>
                    )}
                    {!isCreditNote && creditNoteId && creditNoteStatus === 'exported' && (
                      <span
                        style={{ ...creditBadgeStyle, marginLeft: 6, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={function () { this.navigateToInvoice(creditNoteId); }.bind(this)}
                        title={t('openCreditNote')}
                      >
                        {t('creditedBadge')}
                      </span>
                    )}
                    {!isCreditNote && creditNoteId && creditNoteStatus !== 'exported' && (
                      <span
                        style={{ ...creditPendingBadgeStyle, marginLeft: 6, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={function () { this.navigateToInvoice(creditNoteId); }.bind(this)}
                        title={t('openCreditNote')}
                      >
                        {t('creditPendingBadge')}
                      </span>
                    )}
                    {header.copied_from_id && (
                      <span
                        style={{ ...copyBadgeStyle, marginLeft: 6, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={function () { this.navigateToInvoice(header.copied_from_id); }.bind(this)}
                        title={t('openCopiedFromInvoice')}
                      >
                        {t('copiedFromBadge')} #{header.copied_from_id}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary section */}
              <div style={styles.summaryCard}>
                <div style={styles.summaryGrid}>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>{t('subtotalWork')} {t('vatExclusive')}</span>
                    <span style={styles.summaryValue}>{formatPrice(summary.subtotal_work)}</span>
                    {summary.original_subtotal_work !== summary.subtotal_work && (
                      <span style={styles.summaryOriginal}>
                        {t('originalTotal')}: {formatPrice(summary.original_subtotal_work)}
                      </span>
                    )}
                  </div>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>{t('subtotalParts')} {t('vatExclusive')}</span>
                    <span style={styles.summaryValue}>{formatPrice(summary.subtotal_parts)}</span>
                    {summary.original_subtotal_parts !== summary.subtotal_parts && (
                      <span style={styles.summaryOriginal}>
                        {t('originalTotal')}: {formatPrice(summary.original_subtotal_parts)}
                      </span>
                    )}
                  </div>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>{t('vat')}</span>
                    <span style={styles.summaryValue}>{formatPrice(summary.total_vat)}</span>
                  </div>
                  <div style={styles.summaryItem}>
                    <span style={styles.summaryLabel}>{t('grandTotal')} {t('vatInclusive')}</span>
                    <span style={{ ...styles.summaryValue, fontSize: 22, fontWeight: 700 }}>
                      {formatPrice(summary.total_amount)}
                    </span>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '8px 0 0' }}>
                  {t('pricesExclVatNote')}
                </p>
                {(header.work_hourly_surcharge_applied !== '0.00' || header.parts_markup_percentage_applied !== '0.00') && (
                  <div style={styles.marginInfo}>
                    {header.work_hourly_surcharge_applied !== '0.00' && (
                      <span style={styles.marginTag}>
                        {t('workSurcharge')}: {header.work_hourly_surcharge_applied} {'\u20AC'}/h
                      </span>
                    )}
                    {header.parts_markup_percentage_applied !== '0.00' && (
                      <span style={styles.marginTag}>
                        {t('partsMarkup')}: {header.parts_markup_percentage_applied} %
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Vehicle breakdown */}
              <h4 style={styles.sectionTitle}>{t('vehicleBreakdown')}</h4>
              {vehicles && vehicles.length > 0 ? (
                vehicles.map(function (vehicle) {
                  return (
                    <div key={vehicle.registration} style={styles.vehicleCard}>
                      <div style={styles.vehicleHeader}>
                        <span style={styles.vehicleReg}>{vehicle.registration}</span>
                        {vehicle.make_model && (
                          <span style={styles.vehicleMakeModel}>{vehicle.make_model}</span>
                        )}
                        <span style={styles.vehicleSubtotal}>{formatPrice(vehicle.subtotal)}</span>
                      </div>
                      <div style={styles.vehicleTableWrapper}>
                      <table style={styles.vehicleTable}>
                        <thead>
                          <tr>
                            <th style={styles.vTh}>{t('description')}</th>
                            <th style={styles.vTh}>{t('type')}</th>
                            <th style={{ ...styles.vTh, textAlign: 'right' }}>{t('quantity')}</th>
                            <th style={styles.vTh}>{t('unit')}</th>
                            <th style={{ ...styles.vTh, textAlign: 'right' }}>{t('unitPrice')} {t('vatExclusive')}</th>
                            <th style={{ ...styles.vTh, textAlign: 'right' }}>{t('total')} {t('vatExclusive')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicle.items.map(function (item) {
                            return (
                              <tr key={item.id}>
                                <td style={styles.vTd}>{item.description || '-'}</td>
                                <td style={styles.vTd}>
                                  {item.item_type === 'work' ? t('workItems') : item.item_type === 'part' ? t('partItems') : (item.item_type || '-')}
                                </td>
                                <td style={{ ...styles.vTd, textAlign: 'right' }}>{item.quantity}</td>
                                <td style={styles.vTd}>{item.unit || '-'}</td>
                                <td style={{ ...styles.vTd, textAlign: 'right' }}>{formatPrice(item.unit_price)}</td>
                                <td style={{ ...styles.vTd, textAlign: 'right', fontWeight: 600 }}>
                                  {formatPrice(item.total_price)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={styles.noDataText}>{t('noInvoicesFound')}</p>
              )}
            </div>
          )}

          <CBPromptModal
            modalIsOpen={this.state.exportModalOpen}
            closeModal={function () { this.setState({ exportModalOpen: false }); }.bind(this)}
            performAction={this.handleExportToNetvisor.bind(this)}
            title={t('exportToNetvisor')}
            text={t('confirmExportToNetvisor')}
            buttonText={t('export')}
          />

          <CBPromptModal
            modalIsOpen={this.state.creditModalOpen}
            closeModal={function () { this.setState({ creditModalOpen: false }); }.bind(this)}
            performAction={this.handleCreateCreditNote.bind(this)}
            title={t('createCreditNote')}
            text={t('confirmCreateCreditNote')}
            buttonText={t('createCreditNote')}
          />

          <CBPromptModal
            modalIsOpen={this.state.copyModalOpen}
            closeModal={function () { this.setState({ copyModalOpen: false }); }.bind(this)}
            performAction={this.handleCopyToDraft.bind(this)}
            title={t('copyToDraft')}
            text={t('confirmCopyToDraft')}
            buttonText={t('copyToDraft')}
          />

          <CBPromptModal
            modalIsOpen={this.state.deleteModalOpen}
            closeModal={function () { this.setState({ deleteModalOpen: false }); }.bind(this)}
            performAction={this.handleDelete.bind(this)}
            title={t('delete')}
            text={t('confirmDeleteInvoice')}
            buttonText={t('delete')}
          />

          {this.state.editOpen && (
            <ConsolidatedInvoiceEditDialog
              invoiceId={this.props.invoiceId}
              onClose={function () { this.setState({ editOpen: false }); }.bind(this)}
              onSaved={this.handleEditSaved.bind(this)}
            />
          )}
        </div>
      </div>
    );
  }
}

var styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: 40,
    overflowY: 'auto',
  },
  dialog: {
    backgroundColor: '#0077b0',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    width: '90%',
    maxWidth: 900,
    // minWidth:0 stops the flex item from growing past the viewport to fit a wide
    // line-items table (which would clip the modal); the table scrolls instead.
    minWidth: 0,
    boxSizing: 'border-box',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    marginBottom: 40,
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'sticky',
    top: 0,
    backgroundColor: '#0077b0',
    zIndex: 1,
  },
  dialogTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  dialogContent: {
    padding: '20px 24px',
  },
  errorBanner: {
    backgroundColor: 'rgba(229, 115, 115, 0.15)',
    border: '1px solid rgba(229, 115, 115, 0.4)',
    borderRadius: 8,
    padding: '10px 16px',
    marginBottom: 16,
    color: '#EF9A9A',
    fontSize: 13,
  },
  pilotBanner: {
    backgroundColor: 'rgba(255, 213, 79, 0.15)',
    border: '1px solid rgba(255, 213, 79, 0.4)',
    borderRadius: 8,
    padding: '10px 16px',
    marginBottom: 16,
    color: '#FFE082',
    fontSize: 13,
  },
  exportedInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(100, 181, 246, 0.1)',
    border: '1px solid rgba(100, 181, 246, 0.3)',
    borderRadius: 8,
    padding: '8px 16px',
    marginBottom: 16,
    fontSize: 13,
  },
  exportedLabel: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontWeight: 600,
  },
  exportedValue: {
    color: '#90CAF9',
    marginRight: 16,
  },
  actionsBar: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  actionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    color: '#fff',
    fontWeight: 600,
  },
  headerInfo: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    padding: 20,
    marginBottom: 20,
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 16,
  },
  headerLabel: {
    display: 'block',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  headerValue: {
    display: 'block',
    color: '#fff',
    fontSize: 14,
  },
  summaryCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    padding: 20,
    marginBottom: 24,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 16,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  summaryValue: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
  },
  summaryOriginal: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 11,
    marginTop: 2,
  },
  marginInfo: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
    paddingTop: 12,
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  },
  marginTag: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    margin: '0 0 16px 0',
  },
  vehicleCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: 16,
    marginBottom: 12,
  },
  vehicleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  vehicleReg: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
  },
  vehicleMakeModel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 13,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  vehicleSubtotal: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
  },
  vehicleTableWrapper: {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  vehicleTable: {
    width: '100%',
    minWidth: 520,
    borderCollapse: 'collapse',
  },
  vTh: {
    padding: '6px 8px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    textAlign: 'left',
  },
  vTd: {
    padding: '6px 8px',
    color: '#fff',
    fontSize: 13,
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
  },
  noDataText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 14,
    textAlign: 'center',
    padding: 24,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
  },
};

function mapStateToProps(state) {
  var selectedChainId = state.chain.selectedChainId;
  var user = state.authUser.user;
  var userChains = user && user.chains ? user.chains : [];
  var currentChain = userChains.find(function (c) { return c.id === selectedChainId; });
  return {
    consolidatedInvoicePreview: state.chain.consolidatedInvoicePreview,
    consolidatedInvoicePreviewStatus: state.chain.consolidatedInvoicePreviewStatus,
    exportInvoiceStatus: state.chain.exportInvoiceStatus,
    createCreditNoteStatus: state.chain.createCreditNoteStatus,
    createdCreditNote: state.chain.createdCreditNote,
    copyInvoiceStatus: state.chain.copyInvoiceStatus,
    copiedInvoice: state.chain.copiedInvoice,
    blockExternalWrites: currentChain ? !!currentChain.block_external_writes : false,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchConsolidatedInvoicePreview: _fetchConsolidatedInvoicePreview,
    markConsolidatedInvoiceReady: _markConsolidatedInvoiceReady,
    deleteConsolidatedInvoice: _deleteConsolidatedInvoice,
    exportConsolidatedInvoiceToNetvisor: _exportConsolidatedInvoiceToNetvisor,
    createConsolidatedInvoiceCreditNote: _createConsolidatedInvoiceCreditNote,
    copyConsolidatedInvoiceToDraft: _copyConsolidatedInvoiceToDraft,
  },
)(translate('ChainInvoicingView')(ConsolidatedInvoiceDetail));
